import { useEffect, useState } from "react";
import { Copy, Link2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Panel, Empty } from "@/components/PageHeader";
import { ProfilePlanEditor, CHANNELS, STATE_CLS, emptyPlan } from "@/components/admin/ProfilePlanEditor";
import { ProfileLinkPanel } from "@/components/admin/ProfileLinkPanel";
import { LandingPreview } from "@/components/admin/LandingPreview";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const LEVELS = [["none", "Nessuno"], ["read", "Lettura"], ["write", "Modifica"]];
const LEVEL_CLS = { none: "bg-slate-100 text-slate-400", read: "bg-sky-50 text-sky-700", write: "bg-emerald-50 text-emerald-700" };

export const PermMatrix = ({ sections, value, onChange }) => (
  <div>
    <div className="mb-1.5 flex flex-wrap gap-1 text-[11px]">
      <span className="mr-1 self-center text-slate-400">Imposta tutto:</span>
      {LEVELS.map(([v, l]) => <button key={v} type="button" data-testid={`perm-all-${v}`} onClick={() => onChange(Object.fromEntries(sections.map((s) => [s.key, v])))} className="rounded border border-slate-200 px-2 py-0.5 text-slate-600 hover:bg-slate-50">{l}</button>)}
    </div>
    <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
      {sections.map((s) => (
        <div key={s.key} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
          <span className="text-slate-700">{s.label}</span>
          <div className="flex gap-1">
            {LEVELS.map(([v, l]) => (
              <button key={v} type="button" data-testid={`perm-${s.key}-${v}`} onClick={() => onChange({ ...value, [s.key]: v })}
                className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${(value[s.key] || "none") === v ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const StorePicker = ({ stores, value, onChange }) => {
  const [q, setQ] = useState("");
  const shown = stores.filter((s) => s.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
        <span data-testid="profile-store-count">{value.length ? `${value.length} punti vendita selezionati: il profilo vede e filtra solo questi negozi` : "Nessuna selezione = vede tutti i punti vendita"}</span>
        <span className="flex gap-1">
          <button type="button" data-testid="profile-store-select-all" onClick={() => onChange([...new Set([...value, ...shown.map((s) => s.id)])])} className="rounded border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50">Seleziona {q ? "i filtrati" : "tutti"}</button>
          {value.length > 0 && <button type="button" data-testid="profile-store-clear" onClick={() => onChange([])} className="rounded border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50">Svuota</button>}
        </span>
      </div>
      <Input className="h-8" placeholder="Cerca punto vendita (es. città o insegna)" value={q} onChange={(e) => setQ(e.target.value)} data-testid="profile-store-search" />
      <div className="mt-2 max-h-56 space-y-0.5 overflow-y-auto rounded-lg border border-slate-100 p-1">
        {shown.map((s) => (
          <label key={s.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs hover:bg-slate-50" data-testid={`profile-store-${s.id}`}>
            <Checkbox checked={value.includes(s.id)} onCheckedChange={() => onChange(value.includes(s.id) ? value.filter((x) => x !== s.id) : [...value, s.id])} />
            <span className="truncate">{s.name}</span>
          </label>
        ))}
      </div>
      {value.length > 1 && <p className="mt-2 text-[11px] text-slate-500" data-testid="profile-store-multi-hint">Profilo multi-negozio: gli utenti vedranno i dati aggregati dei {value.length} punti vendita e potranno filtrare per singolo negozio o per gruppo dal pannello Filtri.</p>}
    </div>
  );
};

const ProfileDialog = ({ open, profile, sections, stores, onClose, onSaved }) => {
  const { user } = useAuth();
  const tenantSlug = user?.tenant?.slug || "";
  const [f, setF] = useState(profile);
  const [tab, setTab] = useState("menu");
  useEffect(() => { setF(profile); setTab(profile?._tab || "menu"); }, [profile]);
  if (!f) return null;
  const save = async () => {
    try {
      const body = { ...f, messaging: f.messaging || emptyPlan(), features: f.features || { ai: true, messaging: true } };
      const { data } = f.id ? await api.put(`/admin/profiles/${f.id}`, body) : await api.post("/admin/profiles", body);
      toast.success("Profilo salvato"); onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const nEnabled = Object.values(f.permissions || {}).filter((v) => v && v !== "none").length;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto bg-white" data-testid="profile-dialog">
        <DialogHeader><DialogTitle>{f.id ? `Modifica profilo · ${f.name}` : "Nuovo profilo di accesso"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-slate-500">Nome profilo<Input className="mt-1" data-testid="profile-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
          <label className="text-xs text-slate-500">Descrizione<Input className="mt-1" data-testid="profile-description-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></label>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap">
            <TabsTrigger value="menu" data-testid="profile-tab-menu">Voci di menu ({nEnabled}/{sections.length})</TabsTrigger>
            <TabsTrigger value="stores" data-testid="profile-tab-stores">Punti vendita</TabsTrigger>
            <TabsTrigger value="plan" data-testid="profile-tab-plan">IA e comunicazioni</TabsTrigger>
            <TabsTrigger value="link" data-testid="profile-tab-link">Link di accesso</TabsTrigger>
          </TabsList>
          <TabsContent value="menu" className="mt-4"><div className="mb-1.5 text-xs text-slate-500">Per ogni voce del menu scegli se il profilo non la vede, la consulta (lettura) o può operare (modifica).</div><PermMatrix sections={sections} value={f.permissions || {}} onChange={(p) => setF({ ...f, permissions: p })} /></TabsContent>
          <TabsContent value="stores" className="mt-4"><StorePicker stores={stores} value={f.stores || []} onChange={(s) => setF({ ...f, stores: s })} /></TabsContent>
          <TabsContent value="plan" className="mt-4"><ProfilePlanEditor f={f} setF={setF} usage={f.usage} /></TabsContent>
          <TabsContent value="link" className="mt-4">
            <div className="mb-4 grid gap-3">
              <label className="text-xs text-slate-500">Titolo di benvenuto nella pagina del profilo<Input className="mt-1" data-testid="profile-landing-title" placeholder={`Benvenuto, ${f.name || "profilo"}`} value={f.landing_title || ""} onChange={(e) => setF({ ...f, landing_title: e.target.value })} /></label>
              <label className="text-xs text-slate-500">Testo di benvenuto<Textarea className="mt-1 min-h-[70px]" data-testid="profile-landing-text" placeholder="Da qui accedi al pannello con i dati dei tuoi punti vendita…" value={f.landing_text || ""} onChange={(e) => setF({ ...f, landing_text: e.target.value })} /></label>
              <LandingPreview f={f} tenantSlug={tenantSlug} tenantName={user?.tenant?.name} />
            </div>
            {f.id ? <ProfileLinkPanel profile={f} slug={f.slug || ""} onSlug={(s) => setF({ ...f, slug: s })} onVisual={(v) => setF({ ...f, visual: v })} /> : <p className="text-xs text-slate-500">Salva il profilo per generare il link di accesso e il QR code.</p>}
          </TabsContent>
        </Tabs>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Annulla</Button><Button data-testid="profile-save-button" className="bg-blue-600 hover:bg-blue-700" onClick={save}>Salva profilo</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UsageChips = ({ p }) => {
  const u = p.usage;
  if (!u) return null;
  if (!u.enabled) return <span className="rounded px-1.5 py-0.5 text-[11px] bg-slate-100 text-slate-500" data-testid={`profile-msg-off-${p.id}`}>Comunicazioni disattivate</span>;
  return CHANNELS.map(([k, l]) => { const c = u.channels[k]; if (!c.enabled) return <span key={k} className="rounded px-1.5 py-0.5 text-[11px] bg-slate-100 text-slate-400 line-through">{l}</span>;
    return <span key={k} className={`rounded border px-1.5 py-0.5 text-[11px] ${STATE_CLS[c.state]}`} data-testid={`profile-usage-${p.id}-${k}`}>{l} {c.used}{c.plafond ? `/${c.plafond}` : ""}</span>; });
};

export const ProfilesTab = () => {
  const [rows, setRows] = useState(null);
  const [sections, setSections] = useState([]);
  const [stores, setStores] = useState([]);
  const [edit, setEdit] = useState(null);
  const [reqs, setReqs] = useState({});
  const load = () => { api.get("/admin/profiles").then((r) => setRows(r.data)); api.get("/admin/recharge-requests", { params: { status: "pending" } }).then((r) => setReqs(r.data.reduce((a, x) => ({ ...a, [x.profile_id]: (a[x.profile_id] || 0) + 1 }), {}))).catch(() => {}); };
  useEffect(() => {
    load();
    api.get("/admin/sections").then((r) => setSections(r.data));
    api.get("/stores").then((r) => setStores(r.data)).catch(() => {});
  }, []);
  const del = async (p) => {
    if (!window.confirm(`Eliminare il profilo "${p.name}"?`)) return;
    try { await api.delete(`/admin/profiles/${p.id}`); toast.success("Profilo eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const dup = async (p) => {
    try { const { data } = await api.post(`/admin/profiles/${p.id}/duplicate`); toast.success(`Profilo duplicato: "${data.name}"`); await load(); setEdit(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const label = Object.fromEntries(sections.map((s) => [s.key, s.label]));
  return (
    <Panel title="Profili di accesso" testId="profiles-panel"
      action={<Button size="sm" data-testid="profile-new-button" className="bg-blue-600 hover:bg-blue-700" onClick={() => setEdit({ name: "", description: "", permissions: {}, stores: [], features: { ai: true, messaging: true }, messaging: emptyPlan(), slug: "", landing_title: "", landing_text: "" })}><Plus className="mr-1.5 h-4 w-4" />Nuovo profilo</Button>}>
      {rows && !rows.length && <Empty text="Nessun profilo: creane uno per assegnarlo agli utenti" />}
      <div className="divide-y divide-slate-100">
        {(rows || []).map((p) => (
          <div key={p.id} className="flex flex-col gap-3 px-5 py-4 md:flex-row md:items-start md:justify-between" data-testid={`profile-row-${p.id}`}>
            <div className="min-w-0">
              <div className="font-medium text-slate-800">{p.name} <span className="ml-1 text-xs font-normal text-slate-400">{p.users} utenti · {p.stores.length ? `${p.stores.length} punti vendita` : "tutti i punti vendita"}{p.features?.ai === false ? " · IA disattivata" : ""}{p.logins ? ` · ${p.logins} access${p.logins === 1 ? "o" : "i"} dal link` : ""}</span></div>
              {p.description && <div className="text-xs text-slate-500">{p.description}</div>}
              <div className="mt-2 flex flex-wrap gap-1">
                {Object.entries(p.permissions).filter(([, v]) => v !== "none").map(([k, v]) => <span key={k} className={`rounded px-1.5 py-0.5 text-[11px] ${LEVEL_CLS[v]}`}>{label[k] || k} · {v === "write" ? "modifica" : "lettura"}</span>)}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1"><span className="text-[11px] text-slate-400">Messaggi{p.usage?.mode === "recharge" ? " (pacchetto)" : " (mese)"}:</span><UsageChips p={p} />
                {reqs[p.id] > 0 && <button type="button" onClick={() => setEdit({ ...p, _tab: "plan" })} className="rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-amber-600" data-testid={`profile-requests-${p.id}`}>{reqs[p.id]} richiest{reqs[p.id] === 1 ? "a" : "e"} di ricarica</button>}</div>
            </div>
            <div className="flex shrink-0 gap-1">
              <Button size="sm" variant="outline" data-testid={`profile-link-${p.id}`} title="Link di accesso" onClick={() => setEdit({ ...p, _tab: "link" })}><Link2 className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" data-testid={`profile-duplicate-${p.id}`} title="Duplica profilo" onClick={() => dup(p)}><Copy className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" data-testid={`profile-edit-${p.id}`} onClick={() => setEdit(p)}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" data-testid={`profile-delete-${p.id}`} onClick={() => del(p)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
            </div>
          </div>
        ))}
      </div>
      <ProfileDialog open={!!edit} profile={edit} sections={sections} stores={stores} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </Panel>
  );
};
